import { FormElement } from '../services/api';
import { getUrlParamValue } from './elementValueSource';
import { getVariableValue } from './transactionVariables';

/** Read style key with camelCase or snake_case fallback. */
function getStyleKey(styles: Record<string, any> | undefined, camelKey: string): any {
  if (!styles) return undefined;
  const snakeKey = camelKey.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  return styles[camelKey] !== undefined && styles[camelKey] !== null
    ? styles[camelKey]
    : styles[snakeKey];
}

function matchAllowedValues(
  actual: string,
  allowedRaw: unknown,
  useContains: boolean,
  emptyAllowedMeans: 'require-nonempty' | 'no-match',
): boolean {
  if (allowedRaw == null || String(allowedRaw).trim() === '') {
    if (emptyAllowedMeans === 'require-nonempty') return actual !== '';
    return false;
  }
  const allowed = String(allowedRaw)
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean);
  if (allowed.length === 0) return false;
  if (useContains) return allowed.some((v) => actual.includes(v));
  return allowed.includes(actual.trim());
}

function resolveConditionValue(
  source: 'db' | 'url',
  key: string,
  transactionData: Record<string, any> | null,
): string | undefined {
  if (source === 'url') {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    if (!params.has(key)) return undefined;
    return getUrlParamValue(key);
  }
  return getVariableValue(transactionData, key);
}

export type VisibilityOptions = {
  /**
   * When true and a DB-sourced rule has no transaction data / missing variable,
   * treat show-rules as visible and hide-rules as not-hidden (builder preview).
   */
  treatMissingTxAsPass?: boolean;
};

/** Legacy: show only when URL has param key matching allowed value(s). If no key set, always show. */
export function isElementVisibleByUrlParam(element: FormElement): boolean {
  const key = element.styles?.showByUrlParamKey;
  if (!key || typeof key !== 'string' || key.trim() === '') return true;
  const paramValue = getUrlParamValue(key);
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  // Missing param → hide (same as empty when no allowed list requires non-empty)
  if (!params.has(key.trim())) {
    const allowedValue = element.styles?.showByUrlParamValue;
    if (allowedValue == null || String(allowedValue).trim() === '') return false;
    return false;
  }
  return matchAllowedValues(
    paramValue,
    element.styles?.showByUrlParamValue,
    element.styles?.showByUrlParamContains === true,
    'require-nonempty',
  );
}

/**
 * Show when configured key/value matches.
 * Source: transaction variable (default) or URL query param (`showByVariableSource === 'url'`).
 * If no key set, always show.
 */
export function isElementVisibleByVariable(
  element: FormElement,
  transactionData: Record<string, any> | null,
  options?: VisibilityOptions,
): boolean {
  const styles = element.styles;
  const variableKey =
    (typeof getStyleKey(styles, 'showByVariableKey') === 'string' &&
      String(getStyleKey(styles, 'showByVariableKey')).trim()) ||
    '';
  if (!variableKey) return true;

  const source = getStyleKey(styles, 'showByVariableSource') === 'url' ? 'url' : 'db';
  const varValue = resolveConditionValue(source, variableKey, transactionData);
  if (varValue === undefined) {
    if (source === 'db' && options?.treatMissingTxAsPass) return true;
    return false;
  }

  return matchAllowedValues(
    varValue,
    getStyleKey(styles, 'showByVariableValue'),
    getStyleKey(styles, 'showByVariableContains') === true,
    'no-match',
  );
}

/**
 * Hide when configured key/value matches.
 * Source: transaction variable (default) or URL query param (`hideByVariableSource === 'url'`).
 * If no key set, never hide by this rule.
 */
export function isElementHiddenByVariable(
  element: FormElement,
  transactionData: Record<string, any> | null,
  _options?: VisibilityOptions,
): boolean {
  const styles = element.styles;
  const variableKey =
    (typeof getStyleKey(styles, 'hideByVariableKey') === 'string' &&
      String(getStyleKey(styles, 'hideByVariableKey')).trim()) ||
    '';
  if (!variableKey) return false;

  const source = getStyleKey(styles, 'hideByVariableSource') === 'url' ? 'url' : 'db';
  const varValue = resolveConditionValue(source, variableKey, transactionData);
  if (varValue === undefined) {
    // Missing value → do not hide (including when DB tx data is absent in builder).
    return false;
  }

  return matchAllowedValues(
    varValue,
    getStyleKey(styles, 'hideByVariableValue'),
    getStyleKey(styles, 'hideByVariableContains') === true,
    'no-match',
  );
}

/** Combined visibility: legacy URL show + show-by-variable + hide-by-variable. */
export function isElementVisible(
  element: FormElement,
  transactionData: Record<string, any> | null,
  options?: VisibilityOptions,
): boolean {
  return (
    isElementVisibleByUrlParam(element) &&
    isElementVisibleByVariable(element, transactionData, options) &&
    !isElementHiddenByVariable(element, transactionData, options)
  );
}
